import { useCallback, useEffect, useRef, useState } from "react";
import {
	catalogApi,
	type MethodPaper,
	type Paper,
	type StudentPaper,
} from "@/lib/catalog-api.service";

/**
 * `loading` means "there is nothing to show yet", NOT "a request is in flight".
 *
 * Callers render a spinner in place of the list while `loading` is true. If a
 * post-mutation refetch also raised the flag, every checkbox click would unmount
 * the whole list and remount it a moment later — which is exactly what the page
 * flashing on each toggle was. Keeping the current rows on screen while
 * revalidating makes the update look like the single-row change it is.
 */
function useRevalidating<T>(
	fetcher: () => Promise<T>,
	empty: T,
	errorMessage: string,
	enabled = true,
) {
	const [data, setData] = useState<T>(empty);
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState<string | null>(null);
	// Survives re-renders without triggering one; only the FIRST load blanks out.
	const hasLoaded = useRef(false);

	const refetch = useCallback(async () => {
		if (!enabled) {
			setData(empty);
			setLoading(false);
			return;
		}

		if (!hasLoaded.current) setLoading(true);
		setError(null);
		try {
			setData(await fetcher());
			hasLoaded.current = true;
		} catch (err) {
			setError(err instanceof Error ? err.message : errorMessage);
		} finally {
			setLoading(false);
		}
		// Every dependency here is stable by construction: both callers wrap
		// `fetcher` in useCallback, and `empty`/`errorMessage` are module-level
		// constants. So `refetch` changes identity exactly when the query itself
		// changes (includeInactive / methodId), which is what should refire it.
	}, [enabled, fetcher, empty, errorMessage]);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { data, setData, loading, error, refetch };
}

const NO_PAPERS: Paper[] = [];
const NO_METHOD_PAPERS: MethodPaper[] = [];
const NO_STUDENT_PAPERS: StudentPaper[] = [];

/**
 * The paper catalog itself — used by the admin paper table and pickers.
 *
 * Deliberately uncached. An earlier module-level cache here was never read
 * (its only consumer passes `includeInactive`, which bypassed it) and nothing
 * invalidated it after a create or rename, so adding a second consumer would
 * have silently served a stale list. `refetch` is exposed instead: the caller
 * that just mutated knows exactly when to re-read.
 */
export function usePapers(includeInactive = false) {
	const fetcher = useCallback(() => catalogApi.listPapers(includeInactive), [includeInactive]);
	const { data, loading, error, refetch } = useRevalidating(
		fetcher,
		NO_PAPERS,
		"Không thể tải danh sách hồ sơ",
	);

	return { papers: data, loading, error, refetch };
}

/**
 * The papers required by one diện xét tuyển, via the admission-method
 * mapping endpoint. That endpoint intentionally includes deactivated papers
 * so a student who already has one still sees its name resolved.
 *
 * `setMethodPapers` is exposed so a caller that just replaced the mapping can
 * apply the response it already holds instead of issuing a second round trip.
 */
export function useMethodPapers(methodId: string | undefined) {
	const fetcher = useCallback(() => catalogApi.listMethodPapers(methodId as string), [methodId]);
	const { data, setData, loading, error, refetch } = useRevalidating(
		fetcher,
		NO_METHOD_PAPERS,
		"Không thể tải hồ sơ của diện xét tuyển",
		Boolean(methodId),
	);

	return { methodPapers: data, setMethodPapers: setData, loading, error, refetch };
}

/**
 * The hồ sơ checklist for one student — the backend's union of "currently
 * required by their diện" and "already recorded in GiayTo" (see
 * `catalog.controller.ts#listStudentPapers`).
 *
 * This is what `documents-list.tsx` must use instead of `useMethodPapers`:
 * building the checkbox state from the method mapping alone is exactly the
 * bug this endpoint exists to fix — a paper retired from the mapping, or
 * hidden by a rule the student no longer satisfies, would have no key in
 * that state and silently disappear from `GiayTo` on the next save.
 */
export function useStudentPapers(mssv: string | undefined) {
	const fetcher = useCallback(() => catalogApi.listStudentPapers(mssv as string), [mssv]);
	const { data, loading, error, refetch } = useRevalidating(
		fetcher,
		NO_STUDENT_PAPERS,
		"Không thể tải danh sách hồ sơ của sinh viên",
		Boolean(mssv),
	);

	return { studentPapers: data, loading, error, refetch };
}
